import { useSyncExternalStore } from "react";

export type ConnectionStatus = "online" | "internet-unavailable" | "pax-unavailable";

const listeners = new Set<() => void>();
let paxReachable = true;

function notifyListeners(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  function handleBrowserNetworkChange(): void {
    if (navigator.onLine) paxReachable = true;
    listener();
  }

  window.addEventListener("online", handleBrowserNetworkChange);
  window.addEventListener("offline", handleBrowserNetworkChange);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("online", handleBrowserNetworkChange);
    window.removeEventListener("offline", handleBrowserNetworkChange);
  };
}

export function reportPaxReachable(): void {
  if (paxReachable) return;
  paxReachable = true;
  notifyListeners();
}

function reportPaxUnreachable(): void {
  if (!paxReachable) return;
  paxReachable = false;
  notifyListeners();
}

export async function observePaxRequest<Result>(request: () => Promise<Result>): Promise<Result> {
  try {
    const result = await request();
    reportPaxReachable();
    return result;
  } catch (error) {
    if (error instanceof TypeError) reportPaxUnreachable();
    throw error;
  }
}

export function paxFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return observePaxRequest(() => fetch(input, init));
}

function connectionStatus(): ConnectionStatus {
  if (!navigator.onLine) return "internet-unavailable";
  return paxReachable ? "online" : "pax-unavailable";
}

export function useConnectionStatus(): ConnectionStatus {
  return useSyncExternalStore(subscribe, connectionStatus, () => "online");
}
